/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { RefObject } from "react";
import { useLayoutEffect, useState } from "react";

import { spacing } from "../../global/emotion";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../lib/constants/endOfBrowserAsyncQueue.constants";

type UseScrollableCenterPositionProps = {
  offsetParentRef: RefObject<HTMLElement>;
  centeredElementRef: RefObject<HTMLElement>;
};

/**
 * @param {RefObject<HTMLElement>} offsetParentRef The parent element used for positioning. This element should have
 *    a `position` other than `static`
 * @param {RefObject<HTMLElement>} centeredElementRef The element you intend to center.
 * @param {any[]} dependencies Dependencies for the layout effect used to identify element sizes; include whatever may
 *    change the size of either offsetParentRef or centeredElementRef
 * @return {Interpolation}
 */
export const useScrollableCenterPosition = (
  { offsetParentRef, centeredElementRef }: UseScrollableCenterPositionProps,
  dependencies: any[],
): Interpolation | null => {
  const [centeredElHeight, setCenteredElHeight] = useState<number>(null);

  useLayoutEffect(() => {
    if (typeof window !== "undefined") {
      setTimeout(() => {
        const outerBox = offsetParentRef?.current?.getBoundingClientRect();
        if (outerBox?.height && outerBox.height > window.innerHeight) {
          const innerBox = centeredElementRef.current?.getBoundingClientRect();
          if (innerBox) setCenteredElHeight(innerBox.height);
          else setCenteredElHeight(null);
        } else setCenteredElHeight(null);
      }, END_OF_BROWSER_ASYNC_QUEUE);
    }
  }, dependencies);

  return centeredElHeight === null
    ? null
    : {
        bottom: `calc(50% - ${centeredElHeight / 2}px)`,
        marginBlock: spacing.xxl,
        position: "sticky",
        top: `calc(50% - ${centeredElHeight / 2}px)`,
      };
};
